import React, { useEffect, useRef, useCallback } from'react';
import { useAtozasLanguage } from'../../context/AtozasLanguageContext';
import translateText from'../../utils/translate';

/**
 * Detect if browser is Microsoft Edge
 * @returns {boolean} True if Edge browser
 */
const isEdgeBrowser = () => {
 if (typeof navigator ==='undefined') return false;
 const userAgent = navigator.userAgent.toLowerCase();
 return userAgent.includes('edg/') || userAgent.includes('edgios') || userAgent.includes('edge/');
};

/**
 * Detect if running on mobile device
 * @returns {boolean} True if mobile device
 */
const isMobileDevice = () => {
 if (typeof navigator ==='undefined') return false;
 // Check user agent
 const ua = navigator.userAgent.toLowerCase();
 const mobileUA = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(ua);
 
 // Check screen size (mobile typically < 768px)
 const isSmallScreen = typeof window !=='undefined'&& 
 window.matchMedia && 
 window.matchMedia('(max-width: 768px)').matches;
 
 // Check touch capability
 const hasTouch ='ontouchstart'in window || navigator.maxTouchPoints > 0;
 
 return mobileUA || (isSmallScreen && hasTouch);
};

/**
 * Check if device has low performance (older/slower devices)
 * @returns {boolean} True if low performance device
 */
const isLowPerformanceDevice = () => {
 if (typeof navigator ==='undefined') return false;
 
 // Check hardware concurrency (CPU cores)
 const cores = navigator.hardwareConcurrency || 2;
 
 // Check device memory (if available)
 const memory = navigator.deviceMemory || 4; // Default to 4GB if not available
 
 // Low performance if: < 4 cores OR < 2GB RAM
 return cores < 4 || memory < 2;
};

/**
 * Check if element is visible in viewport
 * @param {Element} element - DOM element to check
 * @returns {boolean} True if element is visible
 */
const isElementVisible = (element) => {
 if (!element || typeof window ==='undefined') return false;
 
 const rect = element.getBoundingClientRect();
 const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
 const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
 
 // Check if element is within viewport (with some margin for better UX)
 const margin = 200;
 return (
 rect.top < viewportHeight + margin &&
 rect.bottom > -margin &&
 rect.left < viewportWidth + margin &&
 rect.right > -margin
 );
};

/**
 * AutoTranslateWrapper - Automatically translates all text content in the app
 * This component wraps the entire app and translates all text nodes automatically
 * No need to add translation hooks to individual components
 */
const AutoTranslateWrapper = ({ children }) => {
 const { language } = useAtozasLanguage();
 const containerRef = useRef(null);
 const translationCache = useRef(new Map());
 const isTranslatingRef = useRef(false);
 const originalTextByNodeRef = useRef(new WeakMap());
 const trackedTextNodesRef = useRef(new Set());
 const pendingTranslateTimeoutRef = useRef(null);
 const needsRerunRef = useRef(false);
 const isEdge = useRef(isEdgeBrowser());
 const isMobile = useRef(isMobileDevice());
 const isLowPerf = useRef(isLowPerformanceDevice());

 // Debug: Log language changes
 useEffect(() => {
 console.log('🌐 AutoTranslateWrapper - Language changed to:', language);
 }, [language]);

 const translateAllTextNodes = useCallback(async () => {
 console.log('🔄 translateAllTextNodes called, language:', language,'isTranslating:', isTranslatingRef.current);
 
 if (isTranslatingRef.current) {
 needsRerunRef.current = true;
 console.log('⏸️ Translation already in progress, will rerun after completion');
 return;
 }
 isTranslatingRef.current = true;

 try {
 // Check if DOM is ready
 if (typeof document ==='undefined'|| !document.body) {
 console.warn('⚠️ Translation skipped: DOM not ready');
 isTranslatingRef.current = false;
 return;
 }

 // Check browser compatibility for createTreeWalker
 if (typeof document.createTreeWalker ==='undefined') {
 console.warn('⚠️ Translation skipped: createTreeWalker not supported in this browser');
 isTranslatingRef.current = false;
 return;
 }

 // If language is English, restore original text and skip translation.
 // CRITICAL: This must happen FIRST before any other processing
 if (language ==='en'|| !language) {
 console.log('ℹ️ Language is English, restoring original text');
 // First, restore tracked nodes
 const nodesToRemove = [];
 let trackedRestoredCount = 0;
 
 trackedTextNodesRef.current.forEach((node) => {
 // Prune detached nodes to avoid memory leaks
 if (!node || !node.isConnected) {
 nodesToRemove.push(node);
 return;
 }

 const original = originalTextByNodeRef.current.get(node);
 if (typeof original ==='string') {
 const currentText = node.textContent ||'';
 // Restore if text is different (might be translated)
 if (currentText !== original) {
 try {
 node.textContent = original;
 trackedRestoredCount++;
 } catch (err) {
 console.warn('Error restoring tracked node text:', err);
 }
 }
 }
 });
 
 // Clean up detached nodes
 nodesToRemove.forEach((node) => {
 trackedTextNodesRef.current.delete(node);
 originalTextByNodeRef.current.delete(node);
 });
 
 if (trackedRestoredCount > 0) {
 console.log(`English restoration: Restored ${trackedRestoredCount} tracked nodes`);
 }
 
 // CRITICAL: If we have no tracked nodes, this might be a fresh page load with English
 // In this case, capture current text as original (assuming it's already English)
 // This handles the case where page loads with English and text is already in English
 if (trackedTextNodesRef.current.size === 0) {
 console.log('ℹ️ No tracked nodes found - capturing current text as original for English');
 try {
 const walker = document.createTreeWalker(
 document.body,
 NodeFilter.SHOW_TEXT,
 {
 acceptNode: (node) => {
 if (!node || !node.textContent || !node.textContent.trim()) {
 return NodeFilter.FILTER_REJECT;
 }
 const parent = node.parentElement;
 if (!parent) return NodeFilter.FILTER_REJECT;
 if (parent.closest('[translate="no"], .notranslate')) {
 return NodeFilter.FILTER_REJECT;
 }
 const tagName = parent.tagName?.toLowerCase();
 if (['script','style','noscript','input','textarea','select','option'].includes(tagName)) {
 return NodeFilter.FILTER_REJECT;
 }
 return NodeFilter.FILTER_ACCEPT;
 }
 }
 );
 
 let node;
 let capturedCount = 0;
 while ((node = walker.nextNode())) {
 if (node && node.isConnected && !originalTextByNodeRef.current.has(node)) {
 const currentText = node.textContent ||'';
 if (currentText.trim()) {
 originalTextByNodeRef.current.set(node, currentText);
 trackedTextNodesRef.current.add(node);
 capturedCount++;
 }
 }
 }
 
 if (capturedCount > 0) {
 console.log(`✅ Captured ${capturedCount} text nodes as original for English`);
 }
 } catch (err) {
 console.warn('Error capturing originals for English:', err);
 }
 }

 // ALL BROWSERS: Scan entire DOM to restore any missed nodes
 // This ensures all translated text is restored, even if not in tracked set
 // CRITICAL: Force restoration even if original text wasn't stored
 try {
 const walker = document.createTreeWalker(
 document.body,
 NodeFilter.SHOW_TEXT,
 {
 acceptNode: (node) => {
 if (!node || !node.textContent || !node.textContent.trim()) {
 return NodeFilter.FILTER_REJECT;
 }
 const parent = node.parentElement;
 if (!parent) return NodeFilter.FILTER_REJECT;
 if (parent.closest('[translate="no"], .notranslate')) {
 return NodeFilter.FILTER_REJECT;
 }
 const tagName = parent.tagName?.toLowerCase();
 if (['script','style','noscript','input','textarea','select','option'].includes(tagName)) {
 return NodeFilter.FILTER_REJECT;
 }
 return NodeFilter.FILTER_ACCEPT;
 }
 }
 );

 let node;
 let restoredCount = 0;
 let skippedCount = 0;
 let alreadyEnglishCount = 0;
 let noOriginalCount = 0;
 
 while ((node = walker.nextNode())) {
 if (!node || !node.isConnected) {
 continue;
 }

 const currentText = node.textContent ||'';
 
 // If we have original text stored, restore it
 if (originalTextByNodeRef.current.has(node)) {
 const original = originalTextByNodeRef.current.get(node);
 if (typeof original ==='string') {
 // Always restore if different (ensures we get back to English)
 if (currentText !== original) {
 try {
 node.textContent = original;
 restoredCount++;
 } catch (err) {
 console.warn('Error restoring text node:', err);
 skippedCount++;
 }
 } else {
 alreadyEnglishCount++;
 }
 }
 } else {
 // CRITICAL FIX: For nodes without stored original during English restoration,
 // if the current text looks like it might be translated (contains non-ASCII characters
 // or doesn't match common English patterns), we should try to restore it.
 // However, since we don't have the original, we'll mark it for potential refresh.
 // But more importantly: if we're switching TO English, the text should already be English
 // UNLESS it was translated. In that case, we need to force a re-render.
 // The best approach: store current text as"original"if it looks like English,
 // so future switches will work correctly.
 noOriginalCount++;
 
 // If current text looks like English (mostly ASCII, common English words),
 // store it as original for future use
 // Check for printable ASCII characters (0x20-0x7E) and whitespace, excluding control characters
 const looksLikeEnglish = /^[\x20-\x7E\s]*$/.test(currentText) && 
 currentText.trim().length > 0 &&
 !currentText.match(/[\u0590-\u05FF\u0600-\u06FF\u4E00-\u9FFF]/); // Not Hebrew, Arabic, or Chinese
 
 if (looksLikeEnglish && currentText.trim()) {
 try {
 originalTextByNodeRef.current.set(node, currentText);
 trackedTextNodesRef.current.add(node);
 } catch (err) {
 // Ignore errors storing original
 }
 }
 }
 }
 
 if (restoredCount > 0 || trackedRestoredCount > 0) {
 console.log(`English restoration complete: Restored ${restoredCount + trackedRestoredCount} nodes (${restoredCount} from scan, ${trackedRestoredCount} from tracked), ${alreadyEnglishCount} already English, ${noOriginalCount} without original, ${skippedCount} skipped`);
 }
 
 // CRITICAL: If we restored nodes, force a re-render to ensure UI updates
 // This ensures the restoration is visible immediately
 if (restoredCount > 0 || trackedRestoredCount > 0) {
 // Force a DOM update by triggering a small delay and re-check
 setTimeout(() => {
 // Double-check that restoration worked and trigger again if needed
 const needsRerun = Array.from(trackedTextNodesRef.current).some((node) => {
 if (!node || !node.isConnected) return false;
 const original = originalTextByNodeRef.current.get(node);
 const current = node.textContent ||'';
 return original && current !== original;
 });
 
 if (needsRerun && (language ==='en'|| !language)) {
 console.log('🔄 Re-running English restoration to catch missed nodes');
 translateAllTextNodes();
 }
 }, 50);
 }
 } catch (walkerError) {
 console.warn('Error scanning DOM for English restoration:', walkerError);
 }
 
 isTranslatingRef.current = false;
 return;
 }

 // Log that we're starting translation for non-English language
 console.log('🌐 Starting translation for language:', language);
 
 // Edge browser: Wait a bit longer for DOM to stabilize
 if (isEdge.current) {
 await new Promise(resolve => setTimeout(resolve, 50));
 }

 // Get all text nodes in the document
 let walker;
 try {
 // Edge browser: Use more lenient TreeWalker options
 const walkerOptions = {
 acceptNode: (node) => {
 try {
 // Skip if node is empty or only whitespace
 if (!node || !node.textContent || !node.textContent.trim()) {
 return NodeFilter.FILTER_REJECT;
 }
 // Skip if node is inside script, style, or noscript tags
 const parent = node.parentElement;
 if (!parent) return NodeFilter.FILTER_REJECT;
 // Skip if inside a region explicitly marked as not translatable
 // (e.g., language selectors, codes, already-translated UI labels).
 if (parent.closest('[translate="no"], .notranslate')) {
 return NodeFilter.FILTER_REJECT;
 }
 const tagName = parent.tagName?.toLowerCase();
 if (['script','style','noscript'].includes(tagName)) {
 return NodeFilter.FILTER_REJECT;
 }
 // Skip if inside input, textarea, or other form elements (they handle their own translation)
 if (['input','textarea','select','option'].includes(tagName)) {
 return NodeFilter.FILTER_REJECT;
 }
 return NodeFilter.FILTER_ACCEPT;
 } catch (err) {
 // Edge browser: Handle errors more gracefully
 if (isEdge.current) {
 console.warn('TreeWalker acceptNode error (Edge):', err);
 return NodeFilter.FILTER_REJECT;
 }
 throw err;
 }
 }
 };

 walker = document.createTreeWalker(
 document.body,
 NodeFilter.SHOW_TEXT,
 walkerOptions
 );
 } catch (walkerError) {
 console.error('Error creating TreeWalker:', walkerError);
 isTranslatingRef.current = false;
 return;
 }

 const textNodes = [];
 let node;
 let nodeCount = 0;
 
 // Mobile/Low performance: More aggressive node limits
 let maxNodes = 1000;
 if (isMobile.current || isLowPerf.current) {
 maxNodes = 300; // Mobile: Much lower limit
 } else if (isEdge.current) {
 maxNodes = 500; // Edge: Moderate limit
 }
 
 try {
 while ((node = walker.nextNode())) {
 nodeCount++;
 if (nodeCount > maxNodes) {
 console.warn(`Translation: Reached max node limit (${maxNodes}), stopping traversal`);
 break;
 }

 // Mobile/Edge browser: Additional safety checks
 if ((isMobile.current || isEdge.current) && (!node || !node.isConnected || !node.parentElement)) {
 continue;
 }
 
 // Mobile: Skip very long text nodes to save memory
 if (isMobile.current && node.textContent && node.textContent.length > 200) {
 continue;
 }

 // Track original text per node so we don't"double translate"on subsequent language changes.
 // CRITICAL: Always store the ORIGINAL English text, never overwrite with translated text
 if (!originalTextByNodeRef.current.has(node)) {
 try {
 // Store the current text as original (should be English on first load)
 const currentText = node.textContent ||'';
 if (currentText.trim()) {
 originalTextByNodeRef.current.set(node, currentText);
 trackedTextNodesRef.current.add(node);
 }
 } catch (err) {
 // Mobile/Edge browser: Handle WeakMap errors gracefully
 if (isMobile.current || isEdge.current) {
 console.warn('Error tracking node (Mobile/Edge):', err);
 continue;
 }
 throw err;
 }
 }
 // IMPORTANT: Never overwrite stored original text
 // The stored original is the English text, and we need it for restoration

 const originalRaw = originalTextByNodeRef.current.get(node) ||'';
 const match = originalRaw.match(/^(\s*)([\s\S]*?)(\s*)$/);
 const leadingWs = match ? match[1] :'';
 const coreText = match ? match[2] : originalRaw;
 const trailingWs = match ? match[3] :'';
 const trimmedCore = coreText.trim();

 if (trimmedCore && trimmedCore.length > 0 && trimmedCore.length < 500) {
 // Only translate short text nodes (avoid translating large blocks)
 textNodes.push({ node, leadingWs, coreText: trimmedCore, trailingWs });
 }
 }
 } catch (traversalError) {
 // Mobile/Edge browser: Handle traversal errors more gracefully
 if (isMobile.current || isEdge.current) {
 console.warn('TreeWalker traversal error (Mobile/Edge):', traversalError);
 // Continue with nodes collected so far
 } else {
 throw traversalError;
 }
 }

 // Log how many text nodes we found
 console.log(`📝 Found ${textNodes.length} text nodes to translate for language: ${language}`);
 
 if (textNodes.length === 0) {
 console.warn('⚠️ No text nodes found to translate!');
 isTranslatingRef.current = false;
 return;
 }

 // Translate text nodes - process ALL in parallel for maximum speed
 // No batching delays - everything happens at once
 let batchSize = 20; // Larger batches for parallel processing
 
 if (isMobile.current || isLowPerf.current) {
 batchSize = 15; // Mobile: Still process in parallel but slightly smaller
 } else if (isEdge.current) {
 batchSize = 20;
 }
 
 const allResults = [];
 console.log(`🚀 Starting PARALLEL translation with batch size: ${batchSize} (no delays)`);
 
 // Separate visible and non-visible nodes for priority processing
 const visibleNodes = [];
 const nonVisibleNodes = [];
 
 textNodes.forEach(({ node: textNode, ...rest }) => {
 if (textNode.parentElement && isElementVisible(textNode.parentElement)) {
 visibleNodes.push({ node: textNode, ...rest });
 } else {
 nonVisibleNodes.push({ node: textNode, ...rest });
 }
 });
 
 // Process ALL nodes in parallel - no sequential batching, maximum speed
 const processNodes = async (nodes) => {
 return Promise.allSettled(
 nodes.map(async ({ node, leadingWs, coreText, trailingWs }) => {
 try {
 // Check if node is still connected before processing
 if (!node || !node.isConnected) {
 return { skipped: true, reason:'node_disconnected'};
 }

 // Check cache first
 const cacheKey =`${coreText}_${language}`;
 if (translationCache.current.has(cacheKey)) {
 const translated = translationCache.current.get(cacheKey);
 if (node && node.isConnected) {
 node.textContent =`${leadingWs}${translated}${trailingWs}`;
 }
 return { cached: true };
 }

 // Translate text
 const translated = await translateText(coreText,'auto', language);
 
 // Only update if translation is different and node is still connected
 if (translated && translated !== coreText && node && node.isConnected) {
 // Cache the translation
 translationCache.current.set(cacheKey, translated);
 node.textContent =`${leadingWs}${translated}${trailingWs}`;
 return { success: true };
 } else if (node && node.isConnected) {
 // Translation returned original text (might be error or same language)
 translationCache.current.set(cacheKey, translated);
 return { success: true, note:'translation_same_as_original'};
 }
 
 return { skipped: true, reason:'node_disconnected_after_translation'};
 } catch (error) {
 // Silently handle errors to not slow down other translations
 return { error: true, message: error.message };
 }
 })
 );
 };

 // Process visible nodes FIRST (user sees these immediately) - ALL in parallel
 if (visibleNodes.length > 0) {
 console.log(`⚡ Processing ${visibleNodes.length} visible nodes in parallel (no delays)...`);
 const visibleResults = await processNodes(visibleNodes);
 allResults.push(...visibleResults);
 }

 // Then process non-visible nodes in parallel (no delay) - ALL at once
 if (nonVisibleNodes.length > 0) {
 console.log(`⚡ Processing ${nonVisibleNodes.length} background nodes in parallel (no delays)...`);
 const backgroundResults = await processNodes(nonVisibleNodes);
 allResults.push(...backgroundResults);
 }

 // Count successes and errors after all batches are processed
 let successCount = 0;
 let errorCount = 0;
 for (let j = 0; j < allResults.length; j++) {
 const result = allResults[j];
 if (result.status ==='fulfilled'&& result.value?.success) {
 successCount++;
 } else if (result.status ==='rejected') {
 errorCount++;
 }
 }

 // Log translation summary
 if (textNodes.length > 0) {
 console.log(`Translation summary: ${successCount} succeeded, ${errorCount} failed, ${textNodes.length - successCount - errorCount} skipped out of ${textNodes.length} total nodes`);
 }
 } catch (error) {
 console.error('Error in auto-translation:', {
 error: error.message,
 stack: error.stack,
 language,
 device: isMobile.current ?'mobile':'desktop',
 lowPerf: isLowPerf.current,
 edge: isEdge.current,
 domReady: typeof document !=='undefined'&& document.body ?'yes':'no',
 online: typeof navigator !=='undefined'&& navigator.onLine !== undefined ? navigator.onLine :'unknown'});
 } finally {
 isTranslatingRef.current = false;
 if (needsRerunRef.current) {
 needsRerunRef.current = false;
 // Allow the DOM to settle before translating again
 // CRITICAL: Only retry if language is not English
 if (language !=='en'&& language) {
 const retryDelay = isMobile.current || isLowPerf.current ? 500 : 0;
 setTimeout(() => {
 // Double-check language hasn't changed to English during delay
 if (language !=='en'&& language) {
 translateAllTextNodes();
 }
 }, retryDelay);
 }
 }
 }
 }, [language]);

 useEffect(() => {
 // Wait for DOM to be fully ready before translating
 const ensureDOMReady = () => {
 if (typeof document ==='undefined'|| !document.body) {
 return false;
 }
 
 // Check if document is in a ready state
 if (document.readyState ==='loading') {
 return false;
 }

 // Additional check: ensure body has content
 if (!document.body.hasChildNodes() && document.readyState !=='complete') {
 return false;
 }

 return true;
 };

 let checkInterval = null;
 let timeout = null;
 let translateTimeout = null;

 // Language change: Immediate trigger for instant translation
 // All translations happen immediately with no delay
 let checkIntervalMs = 50; // Fast check interval for DOM readiness

 // If DOM is not ready, wait for it
 if (!ensureDOMReady()) {
 checkInterval = setInterval(() => {
 if (ensureDOMReady()) {
 clearInterval(checkInterval);
 checkInterval = null;
 // Immediate execution for all languages
 if (language ==='en'|| !language) {
 translateAllTextNodes();
 } else {
 // Immediate translation (no delay)
 translateAllTextNodes();
 }
 }
 }, checkIntervalMs);

 // Timeout (longer for mobile/Edge)
 let timeoutMs = 10000;
 if (isMobile.current || isLowPerf.current) {
 timeoutMs = 20000; // Mobile: Much longer timeout
 } else if (isEdge.current) {
 timeoutMs = 15000;
 }
 timeout = setTimeout(() => {
 if (checkInterval) {
 clearInterval(checkInterval);
 checkInterval = null;
 }
 console.warn('⚠️ Translation: DOM ready check timed out, attempting translation anyway for language:', language);
 translateTimeout = setTimeout(() => {
 if (language && language !=='en') {
 translateAllTextNodes();
 }
 }, 300);
 }, timeoutMs);
 } else {
 // DOM is ready, proceed with translation
 // Immediate execution for all languages
 console.log('✅ DOM is ready, triggering translation for language:', language);
 if (language ==='en'|| !language) {
 // For English, call immediately without delay
 translateAllTextNodes();
 // CRITICAL: Also trigger after a short delay to ensure restoration completes
 // This handles cases where nodes weren't ready on first pass
 translateTimeout = setTimeout(() => {
 console.log('🔄 Fallback English restoration trigger');
 translateAllTextNodes();
 }, 100);
 } else {
 // For other languages, call immediately (no delay)
 // Also add a fallback trigger after a short delay to ensure it runs
 translateAllTextNodes();
 // Fallback: trigger again after 200ms to catch any missed nodes
 translateTimeout = setTimeout(() => {
 console.log('🔄 Fallback translation trigger for language:', language);
 translateAllTextNodes();
 }, 200);
 }
 }

 return () => {
 if (checkInterval) clearInterval(checkInterval);
 if (timeout) clearTimeout(timeout);
 if (translateTimeout) clearTimeout(translateTimeout);
 };
 }, [language, translateAllTextNodes]);

 // Listen for immediate language change events - trigger translation instantly
 useEffect(() => {
 const handleLanguageChange = (event) => {
 const newLanguage = event.detail?.language;
 if (newLanguage) {
 console.log('🌐 Language change event detected, triggering IMMEDIATE translation:', newLanguage);
 // Clear any existing translation state to force fresh translation
 isTranslatingRef.current = false;
 needsRerunRef.current = false;
 
 // For English, trigger immediate restoration
 if (newLanguage ==='en'|| !newLanguage) {
 // Use requestAnimationFrame for immediate execution after current frame
 requestAnimationFrame(() => {
 console.log('🚀 Executing immediate English restoration after language change');
 translateAllTextNodes();
 });
 
 // Also trigger after a short delay to catch any nodes that weren't ready
 setTimeout(() => {
 console.log('🔄 Secondary English restoration trigger after language change');
 translateAllTextNodes();
 }, 100);
 } else {
 // For non-English languages, trigger translation
 requestAnimationFrame(() => {
 console.log('🚀 Executing immediate translation after language change');
 translateAllTextNodes();
 });
 
 setTimeout(() => {
 console.log('🔄 Secondary translation trigger after language change');
 translateAllTextNodes();
 }, 100);
 }
 }
 };

 if (typeof window !=='undefined') {
 window.addEventListener('language-changed', handleLanguageChange);
 return () => {
 window.removeEventListener('language-changed', handleLanguageChange);
 };
 }
 }, [translateAllTextNodes]);

 // CRITICAL: Cancel any pending translations when language changes to English
 useEffect(() => {
 if (language ==='en'|| !language) {
 // Cancel any pending translation timeouts
 if (pendingTranslateTimeoutRef.current) {
 clearTimeout(pendingTranslateTimeoutRef.current);
 pendingTranslateTimeoutRef.current = null;
 }
 // Stop any ongoing translation
 if (isTranslatingRef.current) {
 needsRerunRef.current = false;
 }
 }
 }, [language]);

 // Watch for new content added to DOM (for dynamic content)
 useEffect(() => {
 if (language ==='en'|| !language) {
 return;
 }

 // Check browser compatibility for MutationObserver
 if (typeof MutationObserver ==='undefined') {
 console.warn('MutationObserver not supported - dynamic content translation disabled');
 return;
 }

 // Ensure DOM is ready before setting up observer
 if (typeof document ==='undefined'|| !document.body) {
 // Wait for DOM to be ready
 const checkDOM = setInterval(() => {
 if (document && document.body) {
 clearInterval(checkDOM);
 setupObserver();
 }
 }, 100);

 // Timeout after 5 seconds
 setTimeout(() => {
 clearInterval(checkDOM);
 }, 5000);

 return () => clearInterval(checkDOM);
 }

 const setupObserver = () => {
 let observer;
 try {
 observer = new MutationObserver((mutations) => {
 // CRITICAL: Don't trigger translation if language is English
 // English restoration changes DOM, but we don't want to translate again
 if (language ==='en'|| !language) {
 return; // Skip translation for English
 }

 // Any subtree changes can introduce new UI text (especially on route changes).
 // Debounce aggressively to avoid excess translation calls.
 const shouldTranslate = mutations.some(
 (m) => m.type ==='childList'&& (m.addedNodes?.length > 0 || m.removedNodes?.length > 0)
 );
 if (!shouldTranslate) return;

 if (pendingTranslateTimeoutRef.current) {
 clearTimeout(pendingTranslateTimeoutRef.current);
 }

 // Mobile/Low performance: Much longer debounce delay
 let debounceDelay = 300;
 if (isMobile.current || isLowPerf.current) {
 debounceDelay = 800; // Mobile: Longer debounce to reduce API calls
 } else if (isEdge.current) {
 debounceDelay = 500;
 }
 
 pendingTranslateTimeoutRef.current = setTimeout(() => {
 // Double-check language before translating (in case it changed during debounce)
 // The language variable is from the closure, but since this useEffect
 // recreates the observer when language changes, it should be current
 // However, we also check in translateAllTextNodes which uses the latest language from context
 translateAllTextNodes();
 }, debounceDelay);
 });

 observer.observe(document.body, {
 childList: true,
 subtree: true
 });

 return () => {
 if (observer) {
 observer.disconnect();
 }
 if (pendingTranslateTimeoutRef.current) {
 clearTimeout(pendingTranslateTimeoutRef.current);
 pendingTranslateTimeoutRef.current = null;
 }
 };
 } catch (observerError) {
 console.error('Error setting up MutationObserver:', observerError);
 return () => {
 if (pendingTranslateTimeoutRef.current) {
 clearTimeout(pendingTranslateTimeoutRef.current);
 pendingTranslateTimeoutRef.current = null;
 }
 };
 }
 };

 const cleanup = setupObserver();
 return cleanup;
 }, [language, translateAllTextNodes]);

 // CRITICAL: Capture original English text on initial mount BEFORE any translation
 // This ensures we always have English text stored, even if language is already set to non-English
 // IMPORTANT: Only capture if language is NOT English - if English, we'll capture during first translation
 useEffect(() => {
 // If language is English, don't capture originals yet (might be translated text from previous session)
 // We'll capture originals when user switches to a non-English language
 if (language ==='en'|| !language) {
 console.log('ℹ️ English selected on mount - skipping original capture (will capture on first translation)');
 return;
 }
 
 // Only run once on mount, and only if we haven't captured originals yet
 if (trackedTextNodesRef.current.size > 0) {
 return; // Already captured originals
 }

 // Wait for DOM to be ready
 const captureOriginals = () => {
 if (typeof document ==='undefined'|| !document.body) {
 return false;
 }

 try {
 const walker = document.createTreeWalker(
 document.body,
 NodeFilter.SHOW_TEXT,
 {
 acceptNode: (node) => {
 if (!node || !node.textContent || !node.textContent.trim()) {
 return NodeFilter.FILTER_REJECT;
 }
 const parent = node.parentElement;
 if (!parent) return NodeFilter.FILTER_REJECT;
 if (parent.closest('[translate="no"], .notranslate')) {
 return NodeFilter.FILTER_REJECT;
 }
 const tagName = parent.tagName?.toLowerCase();
 if (['script','style','noscript','input','textarea','select','option'].includes(tagName)) {
 return NodeFilter.FILTER_REJECT;
 }
 return NodeFilter.FILTER_ACCEPT;
 }
 }
 );

 let node;
 let capturedCount = 0;
 while ((node = walker.nextNode())) {
 if (node && node.isConnected && !originalTextByNodeRef.current.has(node)) {
 const currentText = node.textContent ||'';
 if (currentText.trim()) {
 originalTextByNodeRef.current.set(node, currentText);
 trackedTextNodesRef.current.add(node);
 capturedCount++;
 }
 }
 }

 if (capturedCount > 0) {
 console.log(`Captured ${capturedCount} original text nodes on mount`);
 }
 return true;
 } catch (err) {
 console.warn('Error capturing original text on mount:', err);
 return false;
 }
 };

 let retryInterval = null;
 let retryTimeout = null;

 // Try immediately, then retry if DOM not ready
 if (!captureOriginals()) {
 retryInterval = setInterval(() => {
 if (captureOriginals()) {
 clearInterval(retryInterval);
 retryInterval = null;
 }
 }, 100);

 // Stop retrying after 5 seconds
 retryTimeout = setTimeout(() => {
 if (retryInterval) {
 clearInterval(retryInterval);
 retryInterval = null;
 }
 }, 5000);
 }

 return () => {
 if (retryInterval) {
 clearInterval(retryInterval);
 }
 if (retryTimeout) {
 clearTimeout(retryTimeout);
 }
 };
 }, [language]); // Run when language changes (but only capture if non-English)

 // Mobile: Cleanup memory periodically to prevent memory leaks
 useEffect(() => {
 if (!isMobile.current && !isLowPerf.current) return;
 
 const cleanupInterval = setInterval(() => {
 // Clean up detached nodes from tracked set
 const nodesToRemove = [];
 trackedTextNodesRef.current.forEach((node) => {
 if (!node || !node.isConnected) {
 nodesToRemove.push(node);
 }
 });
 nodesToRemove.forEach((node) => {
 trackedTextNodesRef.current.delete(node);
 originalTextByNodeRef.current.delete(node);
 });
 
 // Limit cache size to prevent memory issues on mobile
 if (translationCache.current.size > 1000) {
 const keys = Array.from(translationCache.current.keys());
 const keysToRemove = keys.slice(0, 200);
 keysToRemove.forEach((key) => {
 translationCache.current.delete(key);
 });
 }
 }, 30000); // Every 30 seconds on mobile devices
 
 return () => clearInterval(cleanupInterval);
 }, []);

 return <div ref={containerRef}>{children}</div>;
};

export default AutoTranslateWrapper;

